import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import SafeAnimatePresence from "../../components/ui/SafeAnimatePresence";
import { FiMessageSquare, FiSearch, FiPhone, FiFileText, FiBarChart2, FiZap, FiPlus, FiLock, FiHash, FiMapPin, FiChevronLeft, FiX, FiEdit3, FiAlertTriangle, FiGrid, FiTrendingUp, FiCopy, FiMic, } from "react-icons/fi";
import { RiSparklingFill } from "react-icons/ri";
import { useAmps } from "../../context/AmpContext";
import { useChannels } from "../../context/ChannelContext";
import AddAmpToChannelModal from "./AddAmpToChannelModal";
import type { Amp } from "../chat/chat.types";
import * as ampService from "../../services/ampService";
import { ROUTES } from "../../app/routes";
import { isSupportMode } from "../../utils/featureAccess";
import "../../styles/AmpsGridPage.css";
const AMP_ICON_MAP: Record<string, React.ReactNode> = {
    message: <FiMessageSquare size={22}/>,
    search: <FiSearch size={22}/>,
    phone: <FiPhone size={22}/>,
    file: <FiFileText size={22}/>,
    image: <RiSparklingFill size={22}/>,
    scan: <FiGrid size={22}/>,
    dollar: <FiTrendingUp size={22}/>,
    chart: <FiBarChart2 size={22}/>,
    shield: <FiLock size={22}/>,
    database: <FiHash size={22}/>,
    car: <FiMapPin size={22}/>,
    compare: <FiCopy size={22}/>,
    zap: <FiZap size={22}/>,
};
const COLOR_SWATCHES = [
    "#8B5CF6",
    "#6366F1",
    "#EC4899",
    "#F59E0B",
    "#10B981",
    "#3B82F6",
];
const AVAILABLE_ICONS = [
    "zap",
    "message",
    "search",
    "phone",
    "file",
    "scan",
    "dollar",
    "chart",
    "shield",
    "database",
    "car",
    "compare",
    "image",
];
const SPECIAL_CARDS = [
    {
        id: "__voice__",
        name: "Momentum Voice",
        description: "AI-powered voice agents for inbound service requests and call handling",
        category: "Voice",
        keywords: ["voice", "call", "inbound", "phone", "agent", "service request"],
    },
    {
        id: "__proposal__",
        name: "Quote Proposal",
        description: "Create and send professional insurance quote proposals with e-signatures and tracking.",
        category: "Quote Proposal",
        keywords: ["proposal", "quote", "document", "signature", "insurance"],
    },
    {
        id: "__carrier__",
        name: "Carrier Download",
        description: "Automate carrier data downloads and policy sync directly into your AMS.",
        category: "Carrier Download",
        keywords: ["carrier", "download", "policy", "sync", "ams", "automation"],
    },
];
interface AmpsGridPageProps {
    onCreateAmp?: () => void;
    initialCreateMode?: boolean;
}
const AmpsGridPage: React.FC<AmpsGridPageProps> = ({ initialCreateMode = false, }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { premadeAmps, customAmps, loading, error, createAmp, updateAmp, deleteAmp, fetchCustomAmps, } = useAmps();
    const { getChannelsForAmp, channels } = useChannels();
    const isAgencyOwner = localStorage.getItem("isAgencyOwner") === "true";
    const SPECIAL_CATEGORY_NAMES = [
        "Voice",
        "Momentum Voice",
        "Quote Proposal",
        "Carrier Download",
    ];
    const premadeCategories = useMemo(() => {
        const cats = Array.from(new Set(premadeAmps.map((a) => a.category).filter(Boolean)));
        return (cats as string[]).filter((c) => (isAgencyOwner && isSupportMode()) || c !== "Commission Forecast");
    }, [premadeAmps, isAgencyOwner]);
    const CATEGORIES = useMemo(() => {
        const hasVoice = premadeCategories.includes("Momentum Voice") ||
            premadeCategories.includes("Voice");
        const extra = ["Voice", "Quote Proposal", "Carrier Download"].filter((c) => !premadeCategories.includes(c) && !(c === "Voice" && hasVoice));
        return ["All", ...premadeCategories, ...extra];
    }, [premadeCategories]);
    const CATEGORY_OPTIONS = premadeCategories;
    const [activeCategory, setActiveCategory] = useState("All");
    const [activeAmpTab, setActiveAmpTab] = useState<"premade" | "yours">("premade");
    const [searchQuery, setSearchQuery] = useState("");
    React.useEffect(() => {
        if (activeAmpTab === "yours") {
            fetchCustomAmps();
            if (SPECIAL_CATEGORY_NAMES.includes(activeCategory)) {
                setActiveCategory("All");
            }
        }
    }, [activeAmpTab, fetchCustomAmps]);
    const [createMode, setCreateMode] = useState(initialCreateMode);
    React.useEffect(() => {
        setCreateMode(initialCreateMode);
    }, [initialCreateMode]);
    const [creating, setCreating] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingAmp, setEditingAmp] = useState<Amp | null>(null);
    const [updating, setUpdating] = useState(false);
    const [deleteConfirmAmp, setDeleteConfirmAmp] = useState<Amp | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [generatingPrompt, setGeneratingPrompt] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [addToChannelAmp, setAddToChannelAmp] = useState<Amp | null>(null);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("Support");
    const [description, setDescription] = useState("");
    const [instructions, setInstructions] = useState("");
    const [knowledgeBaseFile, setKnowledgeBaseFile] = useState<File | null>(null);
    const [color, setColor] = useState(COLOR_SWATCHES[0]);
    const [selectedIcon, setSelectedIcon] = useState("zap");
    const [dragActive, setDragActive] = useState(false);
    const [shareWithCompany, setShareWithCompany] = useState(true);
    const voiceAmp = useMemo(() => premadeAmps.find((a) => a.category === "Momentum Voice" || a.category === "Voice") ?? null, [premadeAmps]);
    const proposalAmp = useMemo(() => premadeAmps.find((a) => a.category === "Quote Proposal") ?? null, [premadeAmps]);
    const carrierAmp = useMemo(() => premadeAmps.find((a) => a.category === "Carrier Download") ?? null, [premadeAmps]);
    const handleAddSpecialToChannel = useCallback((e: React.MouseEvent, amp: Amp) => {
        e.stopPropagation();
        if (channels.length === 0) {
            setErrorMessage("Please create a channel first to add Skills");
            setTimeout(() => setErrorMessage(null), 4000);
        }
        else {
            setAddToChannelAmp(amp);
        }
    }, [channels.length]);
    const filteredAmps = useMemo(() => {
        const sourceAmps = (activeAmpTab === "premade" ? premadeAmps : customAmps)
            .filter((a) => !SPECIAL_CATEGORY_NAMES.includes(a.category))
            .filter((a) => (isAgencyOwner && isSupportMode()) ||
            a.category !== "Commission Forecast");
        const q = searchQuery.trim().toLowerCase();
        let result = activeCategory === "All"
            ? sourceAmps
            : sourceAmps.filter((a) => a.category && a.category === activeCategory);
        if (q)
            result = result.filter((a) => a.name.toLowerCase().includes(q) ||
                (a.description || "").toLowerCase().includes(q));
        return result;
    }, [
        premadeAmps,
        customAmps,
        activeCategory,
        activeAmpTab,
        searchQuery,
        isAgencyOwner,
    ]);
    const matchingSpecialCards = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q || activeAmpTab !== "premade" || activeCategory !== "All")
            return [];
        return SPECIAL_CARDS.filter((c) => c.name.toLowerCase().includes(q) ||
            c.description.toLowerCase().includes(q) ||
            c.keywords.some((k) => k.includes(q)));
    }, [searchQuery, activeAmpTab, activeCategory]);
    const handleCardClick = (amp: Amp) => {
        if (amp.category === "Carrier Finder")
            return navigate("/carrier-finder");
        if (amp.category === "Trucking Market Appetite")
            return navigate("/trucking-appetite");
        if (amp.category === "Rate Quoting Skill")
            return navigate(ROUTES.RATE_QUOTE_LINK);
        if (amp.category === "Commission Forecast")
            return navigate(ROUTES.COMMISSION_FORECAST);
        navigate(`/amp/${amp.id}`);
    };
    const resetForm = useCallback(() => {
        setName("");
        setCategory("Support");
        setDescription("");
        setInstructions("");
        setKnowledgeBaseFile(null);
        setColor(COLOR_SWATCHES[0]);
        setSelectedIcon("zap");
        setDragActive(false);
        setShareWithCompany(true);
    }, []);
    const handleCreate = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || creating)
            return;
        setCreating(true);
        try {
            const createdAmp = await createAmp({
                id: editMode && editingAmp ? editingAmp.id : "0",
                name: name.trim(),
                description: description.trim() || `Custom Skill: ${name.trim()}`,
                category,
                icon: selectedIcon,
                color,
                instructions: instructions.trim() || undefined,
                knowledgeBase: knowledgeBaseFile?.name || undefined,
                shareWithCompany,
            } as any);
            await fetchCustomAmps();
            requestAnimationFrame(() => {
                setSuccessMessage(`Skill "${name.trim()}" ${editMode ? "updated" : "created"} successfully!`);
                setTimeout(() => setSuccessMessage(null), 4000);
            });
            setCreateMode(false);
            setEditMode(false);
            setEditingAmp(null);
            setActiveAmpTab("yours");
            resetForm();
        }
        catch (err) {
            console.error("Failed to create Skill:", err);
            setErrorMessage(`Failed to create Skill: ${err instanceof Error ? err.message : "Unknown error"}`);
            setTimeout(() => setErrorMessage(null), 4000);
        }
        finally {
            setCreating(false);
        }
    }, [
        name,
        description,
        category,
        color,
        selectedIcon,
        instructions,
        knowledgeBaseFile,
        createAmp,
        fetchCustomAmps,
        resetForm,
        creating,
        editMode,
        editingAmp,
    ]);
    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        }
        else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith("text/") ||
                file.type === "application/pdf" ||
                file.name.endsWith(".txt") ||
                file.name.endsWith(".pdf") ||
                file.name.endsWith(".doc") ||
                file.name.endsWith(".docx")) {
                setKnowledgeBaseFile(file);
            }
        }
    }, []);
    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setKnowledgeBaseFile(e.target.files[0]);
        }
    }, []);
    const handleEditClick = useCallback((e: React.MouseEvent, amp: Amp) => {
        e.stopPropagation();
        setEditingAmp(amp);
        setName(amp.name);
        setCategory(amp.category);
        setDescription(amp.description || "");
        setInstructions(amp.instructions || "");
        setColor(amp.color);
        setSelectedIcon(amp.icon);
        setEditMode(true);
    }, []);
    const handleUpdate = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || !editingAmp || updating)
            return;
        setUpdating(true);
        try {
            const updatedAmp = await updateAmp(editingAmp.id, {
                name: name.trim(),
                description: description.trim() || `Custom Skill: ${name.trim()}`,
                category,
                icon: selectedIcon,
                color,
                instructions: instructions.trim() || undefined,
                knowledgeBase: knowledgeBaseFile?.name || editingAmp.knowledgeBase || undefined,
            });
            await fetchCustomAmps();
            setEditMode(false);
            setEditingAmp(null);
            setActiveAmpTab("yours");
            resetForm();
            requestAnimationFrame(() => {
                setSuccessMessage(`AMP "${updatedAmp.name}" updated successfully!`);
                setTimeout(() => setSuccessMessage(null), 4000);
            });
        }
        catch (err) {
            console.error("Failed to update AMP:", err);
            setErrorMessage(`Failed to update AMP: ${err instanceof Error ? err.message : "Unknown error"}`);
            setTimeout(() => setErrorMessage(null), 4000);
        }
        finally {
            setUpdating(false);
        }
    }, [
        name,
        description,
        category,
        color,
        selectedIcon,
        instructions,
        knowledgeBaseFile,
        editingAmp,
        updateAmp,
        fetchCustomAmps,
        resetForm,
        updating,
    ]);
    const handleDeleteClick = useCallback((e: React.MouseEvent, amp: Amp) => {
        e.stopPropagation();
        setDeleteConfirmAmp(amp);
    }, []);
    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteConfirmAmp || deleting)
            return;
        const ampName = deleteConfirmAmp.name;
        setDeleting(true);
        try {
            await deleteAmp(deleteConfirmAmp.id);
            setDeleteConfirmAmp(null);
            setSuccessMessage(`AMP "${ampName}" deleted successfully!`);
            setTimeout(() => setSuccessMessage(null), 4000);
        }
        catch (err) {
            console.error("Failed to delete AMP:", err);
        }
        finally {
            setDeleting(false);
        }
    }, [deleteConfirmAmp, deleteAmp, deleting]);
    const handleGeneratePrompt = useCallback(async () => {
        if (generatingPrompt)
            return;
        const userMessage = name.trim() || "Create a system prompt for an AI assistant";
        setGeneratingPrompt(true);
        try {
            const generatedPrompt = await ampService.generatePrompt(userMessage);
            setInstructions(generatedPrompt);
        }
        catch (err) {
            console.error("Failed to generate prompt:", err);
        }
        finally {
            setGeneratingPrompt(false);
        }
    }, [name, generatingPrompt]);
    return (<>
      <SafeAnimatePresence>
        {successMessage && (<motion.div className="success-toast" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <FiZap size={18}/>
            <span>{successMessage}</span>
          </motion.div>)}
      </SafeAnimatePresence>

      <SafeAnimatePresence>
        {errorMessage && (<motion.div className="success-toast success-toast--error" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <FiAlertTriangle size={18}/>
            <span>{errorMessage}</span>
          </motion.div>)}
      </SafeAnimatePresence>

      {createMode || editMode ? (<div className="create-amp">
          <div className="create-amp__header">
            <motion.button className="create-amp__back" onClick={() => {
                setCreateMode(false);
                setEditMode(false);
                setEditingAmp(null);
                resetForm();
                if (location.pathname === ROUTES.AMPS_CREATE) {
                    navigate(ROUTES.AMPS);
                }
            }} whileHover={{ x: -2 }} whileTap={{ scale: 0.97 }}>
              <FiChevronLeft size={16}/>
              Back to Skills
            </motion.button>
          </div>

          <div className="create-amp__layout">
            <motion.form className="create-amp__form" onSubmit={handleCreate} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
              <div className="create-amp__form-title">
                <div className="create-amp__form-icon">
                  {editMode ? <FiEdit3 size={18}/> : <FiZap size={18}/>}
                </div>
                <div>
                  <h2>{editMode ? "Edit Skill" : "Create New Skill"}</h2>
                  <p>
                    {editMode
                ? "Update your AI-Powered Module"
                : "Build a custom AI-Powered Module"}
                  </p>
                </div>
              </div>

              <div className="create-amp__fields">
                <div className="create-amp__row">
                  <div className="create-amp__field create-amp__field--grow">
                    <label className="create-amp__label">Name</label>
                    <input className="create-amp__input" type="text" placeholder="e.g. Claims Analyzer" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required autoFocus/>
                  </div>
                  <div className="create-amp__field create-amp__field--fixed">
                    <label className="create-amp__label">Category</label>
                    <select className="create-amp__input create-amp__select" value={category} onChange={(e) => setCategory(e.target.value)}>
                      <option value="">Select category</option>
                      {CATEGORY_OPTIONS.map((cat) => (<option key={cat} value={cat}>
                          {cat}
                        </option>))}
                    </select>
                  </div>
                </div>

                <div className="create-amp__field">
                  <label className="create-amp__label">Icon</label>
                  <div className="create-amp__icon-picker">
                    {AVAILABLE_ICONS.map((iconName) => (<motion.button key={iconName} type="button" className={`create-amp__icon-option ${selectedIcon === iconName ? "create-amp__icon-option--active" : ""}`} onClick={() => setSelectedIcon(iconName)} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} style={{
                    color: selectedIcon === iconName
                        ? color
                        : "var(--text-secondary)",
                    borderColor: selectedIcon === iconName
                        ? `${color}60`
                        : "var(--border-glass)",
                    background: selectedIcon === iconName
                        ? `${color}15`
                        : "var(--surface-input)",
                }}>
                        {AMP_ICON_MAP[iconName] || <FiZap size={18}/>}
                      </motion.button>))}
                  </div>
                </div>

                <div className="create-amp__field">
                  <label className="create-amp__label">Description</label>
                  <textarea className="create-amp__input create-amp__textarea" placeholder="What does this Skill do?" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} rows={3}/>
                </div>

                <div className="create-amp__field">
                  <label className="create-amp__label">
                    Skills <span className="create-amp__hint">optional</span>
                  </label>
                  <div className="create-amp__textarea-wrapper">
                    <textarea className="create-amp__input create-amp__textarea" placeholder="Skills or behavior instructions for the AI..." value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={10}/>
                    <motion.button type="button" className={`create-amp__ai-btn ${generatingPrompt ? "create-amp__ai-btn--loading" : ""}`} onClick={handleGeneratePrompt} disabled={generatingPrompt} whileHover={{ scale: !generatingPrompt ? 1.08 : 1 }} whileTap={{ scale: !generatingPrompt ? 0.95 : 1 }} title={generatingPrompt
                ? "Generating..."
                : "Generate AI prompt"}>
                      {generatingPrompt ? (<motion.div animate={{ rotate: 360 }} transition={{
                    repeat: Infinity,
                    duration: 1,
                    ease: "linear",
                }}>
                          <RiSparklingFill size={16}/>
                        </motion.div>) : (<RiSparklingFill size={16}/>)}
                    </motion.button>
                  </div>
                </div>

                <div className="create-amp__field">
                  <label className="create-amp__label">
                    Knowledge Base{" "}
                    <span className="create-amp__hint">optional</span>
                  </label>
                  <div className={`create-amp__file-upload ${dragActive ? "create-amp__file-upload--active" : ""} ${knowledgeBaseFile ? "create-amp__file-upload--filled" : ""}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                    <input type="file" id="knowledge-base-file" className="create-amp__file-input create-amp__file-input--hidden" onChange={handleFileSelect} accept=".txt,.pdf,.doc,.docx"/>
                    {knowledgeBaseFile ? (<motion.div className="create-amp__file-display" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                        <div className="create-amp__file-icon">
                          <FiFileText size={18}/>
                        </div>
                        <div className="create-amp__file-info">
                          <p className="create-amp__file-name">
                            {knowledgeBaseFile.name}
                          </p>
                          <p className="create-amp__file-size">
                            {(knowledgeBaseFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <motion.button type="button" className="create-amp__file-remove" onClick={() => setKnowledgeBaseFile(null)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                          <FiX size={16}/>
                        </motion.button>
                      </motion.div>) : (<motion.div className="create-amp__file-placeholder" animate={{ scale: dragActive ? 1.02 : 1 }}>
                        <div className="create-amp__file-icon-empty">
                          <FiFileText size={24}/>
                        </div>
                        <p className="create-amp__file-label-main">
                          Drag and drop your knowledge base
                        </p>
                        <p className="create-amp__file-label-sub">
                          or{" "}
                          <button type="button" className="create-amp__file-browse" onClick={() => document
                    .getElementById("knowledge-base-file")
                    ?.click()}>
                            browse files
                          </button>
                        </p>
                        <p className="create-amp__file-hint">
                          PDF, TXT, DOC, DOCX (Max 10 MB)
                        </p>
                      </motion.div>)}
                  </div>
                </div>

                {!editMode && (<div className="create-amp__field">
                    <label className="create-amp__label">
                      <input type="checkbox" checked={shareWithCompany} onChange={(e) => setShareWithCompany(e.target.checked)} className="create-amp__checkbox"/>
                      Share with Company
                    </label>
                  </div>)}
              </div>

              <div className="create-amp__actions">
                <motion.button className="glass-btn glass-btn-primary glass-btn-primary--purple" type="submit" disabled={!name.trim() || creating || updating} whileHover={{ scale: !creating && !updating ? 1.02 : 1 }} whileTap={{ scale: !creating && !updating ? 0.97 : 1 }}>
                  {editMode ? <FiEdit3 size={15}/> : <FiPlus size={15}/>}
                  {editMode
                ? updating
                    ? "Updating Skill..."
                    : "Update Skill"
                : creating
                    ? "Creating Skill..."
                    : "Create Skill"}
                </motion.button>
              </div>
            </motion.form>

            <motion.div className="create-amp__preview" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{
                duration: 0.35,
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1],
            }}>
              <div className="create-amp__preview-label">Preview</div>

              <div className="create-amp__preview-card">
                <div className="create-amp__preview-icon" style={{
                background: `linear-gradient(135deg, ${color}33, ${color}15)`,
                color,
            }}>
                  {AMP_ICON_MAP[selectedIcon] || <FiZap size={22}/>}
                </div>
                <div className="create-amp__preview-content">
                  <h3>{name || "Untitled Skill"}</h3>
                  <p>
                    {description || "Your Skill description will appear here..."}
                  </p>
                </div>
                <span className="create-amp__preview-badge" style={{ color, borderColor: `${color}40` }}>
                  {category}
                </span>
              </div>

              <div className="create-amp__preview-hints">
                <div className="create-amp__preview-hint">
                  <FiMessageSquare size={13}/>
                  <span>Chat with your custom Skill after creation</span>
                </div>
                <div className="create-amp__preview-hint">
                  <FiZap size={13}/>
                  <span>Instructions shape how the AI responds</span>
                </div>
                <div className="create-amp__preview-hint">
                  <FiFileText size={13}/>
                  <span>Knowledge Base gives it reference material</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>) : (<div className="amps-grid-page">
          <div className="amps-grid-page__header">
            <div className="amps-grid-page__search-wrap">
              <FiSearch size={14} className="amps-grid-page__search-icon"/>
              <input className="amps-grid-page__search-input" type="text" placeholder="Search Skills..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}/>
              {searchQuery && (<button className="amps-grid-page__search-clear" onClick={() => setSearchQuery("")}>
                  <FiX size={12}/>
                </button>)}
            </div>
            <motion.button className="amps-grid-page__create-btn glass-btn glass-btn-primary glass-btn-primary--purple" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => navigate(ROUTES.AMPS_CREATE)}>
              <FiPlus size={16}/>
              Create Skill
            </motion.button>
          </div>

          <div className="amps-grid-page__tabs">
            <motion.button className={`amps-grid-page__tab ${activeAmpTab === "premade" ? "amps-grid-page__tab--active" : ""}`} onClick={() => setActiveAmpTab("premade")} whileHover={{ color: "rgba(255, 255, 255, 0.9)" }}>
              Momentum Skills
            </motion.button>
            <motion.button className={`amps-grid-page__tab ${activeAmpTab === 'yours' ? 'amps-grid-page__tab--active' : ''}`} onClick={() => setActiveAmpTab('yours')} whileHover={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Your Skills
            </motion.button>
          </div>

          <div className="amps-grid-page__filters">
            {CATEGORIES.filter((cat) => activeAmpTab === "premade" ||
                !["Voice", "Quote Proposal", "Carrier Download"].includes(cat)).map((cat) => (<button key={cat} className={`amps-grid-page__filter-pill glass-tag ${activeCategory === cat ? (cat === "Proposals" ? "amps-grid-page__filter-pill--active-teal" : "amps-grid-page__filter-pill--active") : ""}`} onClick={() => setActiveCategory(cat)}>
                {cat}
              </button>))}
          </div>

          {activeCategory === "Quote Proposal" ? (<div className="amps-grid-page__grid">
              <motion.div className="amp-card glass-surface amp-card--accent-teal" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => navigate(ROUTES.QUOTE_PROPOSAL)}>
                {proposalAmp && (<motion.button className="amp-card__add-channel-btn" onClick={(e) => handleAddSpecialToChannel(e, proposalAmp)} title={channels.length === 0
                        ? "Create a Workspace first"
                        : "Add to Workspace"} style={channels.length === 0
                        ? { opacity: 0.5, cursor: "default" }
                        : undefined}>
                    <FiPlus size={14}/>
                  </motion.button>)}
                <div className="amp-card__icon amp-card__icon--teal">
                  <FiFileText size={22}/>
                </div>
                <div className="amp-card__content">
                  <h3 className="amp-card__name amp-card__name--with-badge">
                    Quote Proposal
                    <span className="amp-card__new-badge--teal">
                      NEW
                    </span>
                  </h3>
                  <p className="amp-card__desc">
                    Create and send professional insurance quote proposals with
                    e-signatures and tracking.
                  </p>
                </div>
                <div className="amp-card__tags">
                  <span className="amp-card__tag--teal">
                    # Quote Proposal
                  </span>
                  <span className="amp-card__tag--amber">
                    # Documents
                  </span>
                </div>
                <span className="amp-card__category glass-tag amp-card__category--teal">
                  Quote Proposal
                </span>
              </motion.div>
            </div>) : activeCategory === "Voice" ||
                activeCategory === "Momentum Voice" ? (<div className="amps-grid-page__grid">
              <motion.div className="amp-card glass-surface amp-card--clickable" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => navigate(ROUTES.VOICE)}>
                {voiceAmp && (<motion.button className="amp-card__add-channel-btn" onClick={(e) => handleAddSpecialToChannel(e, voiceAmp)} title={channels.length === 0
                        ? "Create a Workspace first"
                        : "Add to Workspace"} style={channels.length === 0
                        ? { opacity: 0.5, cursor: "default" }
                        : undefined}>
                    <FiPlus size={14}/>
                  </motion.button>)}
                <div className="amp-card__icon amp-card__icon--purple">
                  <FiMic size={22}/>
                </div>
                <div className="amp-card__content">
                  <h3 className="amp-card__name">Momentum Voice</h3>
                  <p className="amp-card__desc">
                    AI-powered voice agents for inbound service requests and
                    call handling
                  </p>
                </div>
                <div className="amp-card__tags">
                  <span className="amp-card__tag--purple">
                    # Voice AI
                  </span>
                </div>
                <span className="amp-card__category glass-tag amp-card__category--purple">
                  Voice
                </span>
              </motion.div>
            </div>) : activeCategory === "Carrier Download" ? (<div className="amps-grid-page__grid">
              <motion.div className="amp-card glass-surface amp-card--accent-indigo" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => navigate(ROUTES.CARRIER_DOWNLOAD)}>
                {carrierAmp && (<motion.button className="amp-card__add-channel-btn" onClick={(e) => handleAddSpecialToChannel(e, carrierAmp)} title={channels.length === 0
                        ? "Create a Workspace first"
                        : "Add to Workspace"} style={channels.length === 0
                        ? { opacity: 0.5, cursor: "default" }
                        : undefined}>
                    <FiPlus size={14}/>
                  </motion.button>)}
                <div className="amp-card__icon amp-card__icon--indigo">
                  <FiZap size={22}/>
                </div>
                <div className="amp-card__content">
                  <h3 className="amp-card__name amp-card__name--with-badge">
                    Carrier Download
                    <span className="amp-card__new-badge--indigo">
                      NEW
                    </span>
                  </h3>
                  <p className="amp-card__desc">
                    Automate carrier data downloads and policy sync directly
                    into your AMS.
                  </p>
                </div>
                <div className="amp-card__tags">
                  <span className="amp-card__tag--indigo">
                    # Carrier
                  </span>
                  <span className="amp-card__tag--amber">
                    # Automation
                  </span>
                </div>
                <span className="amp-card__category glass-tag amp-card__category--indigo">
                  Carrier Download
                </span>
              </motion.div>
            </div>) : (<SafeAnimatePresence>
              <div className="amps-grid-page__grid">
                
                {matchingSpecialCards.map((sc) => sc.id === "__voice__" ? (<motion.div key="__voice__" className="amp-card glass-surface amp-card--clickable" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => navigate(ROUTES.VOICE)}>
                      <div className="amp-card__icon amp-card__icon--purple">
                        <FiMic size={22}/>
                      </div>
                      <div className="amp-card__content">
                        <h3 className="amp-card__name">Momentum Voice</h3>
                        <p className="amp-card__desc">
                          AI-powered voice agents for inbound service requests
                          and call handling
                        </p>
                      </div>
                      <div className="amp-card__tags">
                        <span className="amp-card__tag--purple">
                          # Voice AI
                        </span>
                      </div>
                      <span className="amp-card__category glass-tag amp-card__category--purple">
                        Voice
                      </span>
                    </motion.div>) : sc.id === "__proposal__" ? (<motion.div key="__proposal__" className="amp-card glass-surface amp-card--accent-teal" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => navigate(ROUTES.QUOTE_PROPOSAL)}>
                      <div className="amp-card__icon amp-card__icon--teal">
                        <FiFileText size={22}/>
                      </div>
                      <div className="amp-card__content">
                        <h3 className="amp-card__name">Quote Proposal</h3>
                        <p className="amp-card__desc">
                          Create and send professional insurance quote proposals
                          with e-signatures and tracking.
                        </p>
                      </div>
                      <div className="amp-card__tags">
                        <span className="amp-card__tag--teal">
                          # Quote Proposal
                        </span>
                      </div>
                      <span className="amp-card__category glass-tag amp-card__category--teal">
                        Quote Proposal
                      </span>
                    </motion.div>) : (<motion.div key="__carrier__" className="amp-card glass-surface amp-card--accent-indigo" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => navigate(ROUTES.CARRIER_DOWNLOAD)}>
                      <div className="amp-card__icon amp-card__icon--indigo">
                        <FiZap size={22}/>
                      </div>
                      <div className="amp-card__content">
                        <h3 className="amp-card__name">Carrier Download</h3>
                        <p className="amp-card__desc">
                          Automate carrier data downloads and policy sync
                          directly into your AMS.
                        </p>
                      </div>
                      <div className="amp-card__tags">
                        <span className="amp-card__tag--indigo">
                          # Carrier
                        </span>
                      </div>
                      <span className="amp-card__category glass-tag amp-card__category--indigo">
                        Carrier Download
                      </span>
                    </motion.div>))}
                {filteredAmps.map((amp, i) => {
                    const ampChannels = getChannelsForAmp(amp.id);
                    return (<motion.div key={amp.id} className="amp-card glass-surface" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04, duration: 0.3 }} whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => handleCardClick(amp)}>
                      
                      {activeAmpTab === "yours" && (<div className="amp-card__actions">
                          <motion.button className="amp-card__action-btn amp-card__action-btn--edit" onClick={(e) => handleEditClick(e, amp)} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} title="Edit AMP">
                            <FiEdit3 size={14}/>
                          </motion.button>
                          <motion.button className="amp-card__action-btn amp-card__action-btn--delete" onClick={(e) => handleDeleteClick(e, amp)} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} title="Delete AMP">
                            <FiX size={14}/>
                          </motion.button>
                        </div>)}

                      <motion.button className="amp-card__add-channel-btn" onClick={(e) => {
                            e.stopPropagation();
                            if (channels.length === 0) {
                                setErrorMessage("Please create a channel first to add AMPs");
                                setTimeout(() => setErrorMessage(null), 4000);
                            }
                            else {
                                setAddToChannelAmp(amp);
                            }
                        }} title={channels.length === 0
                            ? "Create a Workspace first"
                            : "Add to Workspace"} style={channels.length === 0
                            ? { opacity: 0.5, cursor: "default" }
                            : undefined}>
                        <FiPlus size={14}/>
                      </motion.button>

                      <div className="amp-card__icon" style={{
                            background: `linear-gradient(135deg, ${amp.color}33, ${amp.color}15)`,
                            color: amp.color,
                        }}>
                        {AMP_ICON_MAP[amp.icon] || <FiZap size={22}/>}
                      </div>
                      <div className="amp-card__content">
                        <h3 className="amp-card__name">{amp.name}</h3>
                        <p className="amp-card__desc">
                          {amp.description
                            ?.replace(/\$\$[A-Z_]+(?:START|END)\$\$[\s\S]*?\$\$[A-Z_]+END\$\$/g, "")
                            .replace(/\$\$[A-Z_]+(?:START|END)\$\$[\s\S]*/g, "")
                            .trim()}
                        </p>
                      </div>

                      {ampChannels.length > 0 && (<div className="amp-card__channels">
                          {ampChannels.slice(0, 2).map((channel) => (<span key={channel.guid} className="amp-card__channel-badge" style={{
                                    borderColor: `${channel.color}40`,
                                    color: channel.color,
                                }}>
                              <FiHash size={10}/>
                              {channel.name}
                            </span>))}
                          {ampChannels.length > 2 && (<span className="amp-card__channel-badge amp-card__channel-badge--more">
                              +{ampChannels.length - 2}
                            </span>)}
                        </div>)}

                      <span className="amp-card__category glass-tag" style={{
                            color: amp.color,
                            borderColor: `${amp.color}40`,
                        }}>
                        {amp.category}
                      </span>
                    </motion.div>);
                })}
              </div>
            </SafeAnimatePresence>)}

          {loading && activeAmpTab === "yours" && (<div className="amps-grid-page__empty">
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: "linear" }}>
                <FiZap size={32}/>
              </motion.div>
              <p>Loading your Skills...</p>
            </div>)}

          {error && activeAmpTab === "yours" && (<div className="amps-grid-page__empty">
              <FiZap size={32} className="amps-grid-page__error-icon"/>
              <p>{error}</p>
            </div>)}

          {filteredAmps.length === 0 &&
                !loading &&
                !error &&
                !SPECIAL_CATEGORY_NAMES.includes(activeCategory) && (<div className="amps-grid-page__empty">
                <FiZap size={32}/>
                <p>No Skills found in this category</p>
              </div>)}

          <SafeAnimatePresence>
            {deleteConfirmAmp && (<motion.div className="delete-confirm__backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDeleteConfirmAmp(null)}>
                <motion.div className="delete-confirm__modal glass-elevated" initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()}>
                  <div className="delete-confirm__icon">
                    <FiAlertTriangle size={40}/>
                  </div>
                  <h3 className="delete-confirm__title">Delete AMP</h3>
                  <p className="delete-confirm__message">
                    Are you sure you want to delete{" "}
                    <strong>{deleteConfirmAmp.name}</strong>? This action cannot
                    be undone.
                  </p>
                  <div className="delete-confirm__actions">
                    <motion.button className="delete-confirm__btn delete-confirm__btn--cancel" onClick={() => setDeleteConfirmAmp(null)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled={deleting}>
                      Cancel
                    </motion.button>
                    <motion.button className="delete-confirm__btn delete-confirm__btn--delete" onClick={handleDeleteConfirm} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled={deleting}>
                      {deleting ? "Deleting..." : "Delete AMP"}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>)}
          </SafeAnimatePresence>

          
          <AddAmpToChannelModal isOpen={addToChannelAmp !== null} amp={addToChannelAmp} onClose={() => setAddToChannelAmp(null)}/>
        </div>)}
    </>);
};
export default AmpsGridPage;
